import React from 'react';
import {
  Box,
  Typography,
  Switch,
  IconButton,
  Chip,
  Collapse,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Star as StarIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatDistanceToNow } from 'date-fns';
import { WritingStyle, IllustrationStyle, ToggleCategory } from './types';
import { CATEGORY_PILL_COLORS } from './constants';
import { colors } from '../../../theme/designTokens';
import PromptExpandSection from './PromptExpandSection';
import { ModelCatalogEntry } from './useModelRegistry';

interface StyleCardProps {
  item: WritingStyle | IllustrationStyle;
  position: number;
  inGrid: boolean;
  isMostPopular: boolean;
  isExpanded: boolean;
  styleType: 'writing' | 'illustration';
  toggleCategory: ToggleCategory;
  onToggle: (id: number, currentActive: boolean, category: ToggleCategory) => void;
  onEdit: (style: WritingStyle | IllustrationStyle, type: 'writing' | 'illustration') => void;
  onDelete: (id: number, type: 'writing' | 'illustration') => void;
  onExpandToggle: (id: number) => void;
  onUpdateModelPreference?: (id: number, category: ToggleCategory, modelPreference: string | null) => void;
  modelCatalog?: ModelCatalogEntry[];
}

function costLabel(m: ModelCatalogEntry): string {
  if (!m.pricing) return '';
  const unit = m.pricing.unit.includes('1M')
    ? '1M'
    : m.pricing.unit.includes('image')
      ? 'img'
      : m.pricing.unit.includes('second')
        ? 's'
        : m.pricing.unit;
  return `$${m.pricing.amount}/${unit}`;
}

const StyleCard: React.FC<StyleCardProps> = ({
  item,
  position,
  inGrid,
  isMostPopular,
  isExpanded,
  styleType,
  toggleCategory,
  onToggle,
  onEdit,
  onDelete,
  onExpandToggle,
  onUpdateModelPreference,
  modelCatalog = [],
}) => {
  const capability: 'text' | 'image' = styleType === 'writing' ? 'text' : 'image';
  const applicableModels = modelCatalog.filter(m => m.capabilities.includes(capability));
  const currentModelPref = (item as any).modelPreference as string | null | undefined;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const pillColor = CATEGORY_PILL_COLORS[item.category] || { bg: '#6B7280', color: '#fff' };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: inGrid ? '#fff' : '#f5f0e8',
        borderRadius: '10px',
        border: isOver
          ? '2px dashed #1A5F7A'
          : inGrid
            ? '1px solid #e2e8f0'
            : '1px solid #d6cfc2',
        borderLeft: inGrid ? `3px solid ${colors.deepTeal[500]}` : `3px solid #ccc`,
        opacity: isDragging ? 0.28 : item.isActive ? 1 : 0.48,
        transform: isDragging ? 'scale(0.96)' : undefined,
        boxShadow: isDragging
          ? '0 8px 24px rgba(0,0,0,0.15)'
          : isOver
            ? '0 0 0 2px rgba(26,95,122,0.2)'
            : '0 1px 3px rgba(0,0,0,0.06)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 150ms ease, opacity 150ms ease, border-color 150ms ease',
      }}
    >
      {/* Most Popular ribbon */}
      {isMostPopular && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bgcolor: colors.warmGold[500],
            color: '#fff',
            px: 1,
            py: 0.25,
            fontSize: '0.62rem',
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 700,
            borderRadius: '0 9px 0 6px',
            display: 'flex',
            alignItems: 'center',
            gap: 0.3,
            zIndex: 2,
          }}
        >
          <StarIcon sx={{ fontSize: 10 }} />
          Most Popular
        </Box>
      )}

      {/* Card body */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 1.5, gap: 1.2 }}>
        {/* Drag handle */}
        <Box
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            pt: 0.3,
            color: '#94a3b8',
            userSelect: 'none',
            '&:hover': { color: '#64748b' },
          }}
        >
          <Typography sx={{ fontSize: '1rem', lineHeight: 1, letterSpacing: '-0.05em' }}>&#x28FF;</Typography>
        </Box>

        {/* Position circle */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: inGrid ? colors.deepTeal[500] : '#d1d5db',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: '"Montserrat", sans-serif',
            fontWeight: 700,
            fontSize: '0.72rem',
            flexShrink: 0,
            mt: 0.2,
          }}
        >
          {position}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: '#1e293b',
              lineHeight: 1.3,
              mb: 0.3,
            }}
          >
            {item.name}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#64748b',
              display: '-webkit-box',
              WebkitLineClamp: isExpanded ? 'unset' : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.4,
              mb: 1,
            }}
          >
            {item.description}
          </Typography>

          {/* Footer row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={item.category.toUpperCase()}
              sx={{
                height: 20,
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 700,
                fontSize: '0.6rem',
                letterSpacing: '0.04em',
                bgcolor: pillColor.bg,
                color: pillColor.color,
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
            <Tooltip title={item.isActive ? 'Deactivate' : 'Activate'}>
              <Switch
                checked={item.isActive}
                onChange={() => onToggle(item.id, item.isActive, toggleCategory)}
                size="small"
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: colors.deepTeal[500] } }}
              />
            </Tooltip>
            <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontFamily: '"Montserrat", sans-serif' }}>
              {item.usageCount.toLocaleString()} uses
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontFamily: '"Montserrat", sans-serif' }}>
              {formatDistanceToNow(new Date(item.updatedAt), { addSuffix: true })}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <IconButton size="small" onClick={() => onExpandToggle(item.id)} sx={{ p: 0.3 }}>
              {isExpanded ? <KeyboardArrowUp sx={{ fontSize: 18 }} /> : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
            </IconButton>
            <IconButton size="small" onClick={() => onEdit(item, styleType)} sx={{ p: 0.3, color: colors.deepTeal[500] }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(item.id, styleType)} sx={{ p: 0.3, color: '#ef4444' }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Expanded prompt section */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2, pb: 2, borderTop: '1px solid #e2e8f0' }}>
          {onUpdateModelPreference && applicableModels.length > 0 && (
            <Box sx={{ mt: 1.5, mb: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id={`model-pref-${item.id}-label`}>
                  xAI model override (defaults to global)
                </InputLabel>
                <Select
                  labelId={`model-pref-${item.id}-label`}
                  label="xAI model override (defaults to global)"
                  value={currentModelPref || ''}
                  onChange={(e) => {
                    const raw = String(e.target.value || '');
                    const next = raw === '' ? null : raw;
                    onUpdateModelPreference(item.id, toggleCategory, next);
                  }}
                >
                  <MenuItem value="">
                    <em>— use global default —</em>
                  </MenuItem>
                  {applicableModels.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography sx={{ fontSize: '0.82rem', flex: 1 }}>{m.id}</Typography>
                        <Chip size="small" label={m.tier} sx={{ height: 16, fontSize: '0.58rem', textTransform: 'uppercase' }} />
                        <Typography sx={{ fontSize: '0.68rem', color: '#64748b', minWidth: 60, textAlign: 'right' }}>
                          {costLabel(m)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <PromptExpandSection style={item} type={styleType} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default StyleCard;
